import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import KeyboardEventHandler from "@infinium/react-keyboard-event-handler";
import { useMediaQuery } from "react-responsive";
import { RemoveScroll } from "react-remove-scroll";
import useWindowSize from "../../hooks/useWindowSize";
import { matchOverlayRoute } from "../Routes";
import { OverlayContext } from "../../functional-components/OverlayContext";
import ErrorBoundary from "../../functional-components/ErrorBoundary";
import { ErrorDisplaySmall } from "../ErrorDisplay";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import "./style.scss";
import Icon from "../Icon";

const GenericFlyout = ({
  id,
  isOpen = false,
  toggle = () => {},
  excludeFromClose = "",
  fillHeight = false,
  minHeight = 100,
  width = 500,
  noCloseBtn = false,
  align = "top",
  offset = 0,
  isMobileSubMenu = false,
  children
}) => {
  const t = useBjelinTranslations();
  const isSmall = useMediaQuery({ maxWidth: 767.98 });
  const winDim = useWindowSize();
  const { registerFlyoutOpened, registerFlyoutClosed, overlayOpened } =
    useContext(OverlayContext);

  // Close on location change while open
  const location = useLocation();
  const [first, setFirst] = useState(true);
  const [below, setBelow] = useState(false);
  const prevLocation = useRef(false);
  const _isOpen = useRef(isOpen);
  const modalRoot = useRef();

  useEffect(() => {
    _isOpen.current = isOpen;
  }, [isOpen]);

  const handleLocationChange = useCallback(() => {
    const isOverlayRoute = matchOverlayRoute(location) !== undefined;
    if (isOverlayRoute) {
      prevLocation.current = { ...location };
      setBelow(_isOpen.current);
    } else {
      const wasOverlayRoute =
        prevLocation !== false &&
        matchOverlayRoute(prevLocation.current) !== undefined;
      //Persist flyout when overlay is present and the flyout is below the overlay (i e the flyout triggered the overlay)
      if (_isOpen.current && (!wasOverlayRoute || !below)) {
        toggle();
      }
      prevLocation.current = false;
    }
  }, [below, location, toggle]);

  useEffect(() => {
    if (first) {
      setFirst(false);
    } else {
      setTimeout(() => handleLocationChange(), 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, first]);

  useEffect(() => {
    if (isOpen) {
      setBelow(false);
      registerFlyoutOpened();
    } else {
      registerFlyoutClosed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const mainContainer = document.body;
  if (!modalRoot.current) {
    modalRoot.current = document.getElementById("flyout-portal-root");
  }
  if (!modalRoot.current) {
    modalRoot.current = document.createElement("div");
    modalRoot.current.setAttribute("id", "flyout-portal-root");
    mainContainer.append(modalRoot.current);
  }

  const docClickHandler = useCallback(
    e => {
      if (_isOpen.current) {
        const modalElements = document.getElementsByClassName("modal");
        if (
          !modalRoot.current.contains(e.target) &&
          !(modalElements[0] && modalElements[0].contains(e.target)) &&
          (!excludeFromClose ||
            !document.getElementById(excludeFromClose).contains(e.target))
        ) {
          if (!(below && overlayOpened)) {
            toggle();
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [excludeFromClose, below, overlayOpened, toggle]
  );

  useEffect(() => {
    document.body.addEventListener("click", docClickHandler);
    return () => {
      document.body.removeEventListener("click", docClickHandler);
    };
  }, [docClickHandler]);

  if (!modalRoot.current) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key={"flyout-cover-" + id}
          id="flyout-cover"
          style={{
            zIndex: below ? 0 : 1201,
            top: `calc(var(--padding-top) + ${
              overlayOpened ? 0 : 64
            }px + ${offset}px)`
          }}
          initial={{ opacity: 0, pointerEvents: "all" }}
          animate={{ opacity: 1, pointerEvents: "all" }}
          exit={{ opacity: 0, pointerEvents: "none" }}
          transition={{ type: "spring", damping: 40, stiffness: 400 }}
          onClick={e => {
            if (e.target.id === "flyout-cover") {
              toggle();
            }
            e.stopPropagation();
          }}
        ></motion.div>
      )}
      {isOpen && (
        <motion.div
          key={"flyout-content-" + id}
          id={id ? id : "flyout-content"}
          className={"flyout-content"}
          initial={{
            opacity: 0,
            ...((isMobileSubMenu && isSmall) || align === "left"
              ? { x: "-100%" }
              : { y: "-100%" }),
            pointerEvents: "none"
          }}
          animate={{
            opacity: 1,
            ...((isMobileSubMenu && isSmall) || align === "left"
              ? { x: 0 }
              : { y: 0 }),
            pointerEvents: "all"
          }}
          exit={{
            opacity: 0,
            ...((isMobileSubMenu && isSmall) || align === "left"
              ? { x: "-100%" }
              : { y: "-100%" }),
            pointerEvents: "none"
          }}
          transition={{ type: "spring", damping: 40, stiffness: 400 }}
          style={{
            zIndex: below ? 11 : 1202,
            ...(isSmall
              ? {
                  top: `calc(var(--menu-height) + var(--padding-top) + ${offset}px)`,
                  bottom: 0,
                  left: 0,
                  right: 0
                }
              : {
                  top: `calc(var(--padding-top) + ${
                    overlayOpened ? 0 : 64
                  }px + ${offset}px)`,
                  minHeight: minHeight,
                  maxHeight: `calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) - var(--menu-height) - var(--padding-top))`,
                  ...(fillHeight && {
                    bottom: 0
                  }),
                  ...(align === "left"
                    ? { left: 0 }
                    : align === "top-left"
                    ? {
                        left:
                          winDim.innerWidth > 1280
                            ? winDim.innerWidth / 2 - 640
                            : 0,
                        width,
                        maxWidth: "100vw"
                      }
                    : {
                        right:
                          align === "top-center"
                            ? winDim.innerWidth > width
                              ? (winDim.innerWidth - width) / 2
                              : 0
                            : winDim.innerWidth > 1280
                            ? winDim.innerWidth / 2 - 640
                            : 0,
                        width,
                        maxWidth: "100vw"
                      })
                })
          }}
        >
          {(!noCloseBtn || (isMobileSubMenu && isSmall)) && (
            <button
              className={"btn-close"}
              style={{
                float: "none",
                position: "absolute",
                zIndex: 10,
                top: "1rem",
                lineHeight: "2rem",
                ...(isMobileSubMenu
                  ? {
                      left: "1.5rem",
                      display: "flex",
                      columnGap: "0.3rem"
                    }
                  : {
                      right: "1.5rem"
                    })
              }}
              onClick={() => {
                toggle();
              }}
            >
              {isMobileSubMenu ? (
                <>
                  <Icon name="chevron-left" type="fal" />{" "}
                  <span
                    style={{
                      display: "inline-block",
                      height: "2rem",
                      lineHeight: "2rem",
                      fontSize: "1rem",
                      fontWeight: 400,
                      verticalAlign: "top"
                    }}
                  >
                    {t("back")}
                  </span>
                </>
              ) : (
                <Icon name="xmark" type="fal" />
              )}
            </button>
          )}
          <ErrorBoundary
            boundaryId={"flyout-" + (id ? id : "unspecified")}
            fallback={<ErrorDisplaySmall />}
          >
            {" "}
            <RemoveScroll enabled={!below && offset === 0}>
              {children}{" "}
            </RemoveScroll>
          </ErrorBoundary>
          <KeyboardEventHandler
            handleKeys={["esc"]}
            onKeyEvent={(key, _e) => {
              switch (key) {
                case "esc":
                  toggle();
                  break;
                default:
              }
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>,
    modalRoot.current
  );
};

export default GenericFlyout;
